import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Button from "../../../../components/ui/Button/Button";
import { heroData } from "../../../../data/hero";
import "./Hero.css";

gsap.registerPlugin(ScrollTrigger);

const Hero = ({ ready }) => {
  const sectionRef = useRef(null);
  const badgeRef = useRef(null);
  const headlineRef = useRef(null);
  const subheadlineRef = useRef(null);
  const buttonsRef = useRef(null);
  const backgroundRef = useRef(null);

  useEffect(() => {
    if (!ready) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ delay: 0.1 });

      // Background reveals first
      tl.fromTo(
        backgroundRef.current,
        { opacity: 0, scale: 1.08 },
        { opacity: 1, scale: 1, duration: 1.6, ease: "power2.out" },
      )
        .fromTo(
          badgeRef.current,
          { opacity: 0, y: 24 },
          { opacity: 1, y: 0, duration: 0.7, ease: "power3.out" },
          "-=1.0",
        )
        .fromTo(
          headlineRef.current,
          { opacity: 0, y: 32 },
          { opacity: 1, y: 0, duration: 0.8, ease: "power3.out" },
          "-=0.5",
        )
        .fromTo(
          subheadlineRef.current,
          { opacity: 0, y: 24 },
          { opacity: 1, y: 0, duration: 0.7, ease: "power3.out" },
          "-=0.5",
        )
        .fromTo(
          buttonsRef.current,
          { opacity: 0, y: 16 },
          { opacity: 1, y: 0, duration: 0.6, ease: "power3.out" },
          "-=0.4",
        )
        // After intro completes, set up scroll fade
        .add(() => {
          gsap.fromTo(
            backgroundRef.current,
            { opacity: 1 },
            {
              opacity: 0.15,
              scrollTrigger: {
                trigger: sectionRef.current,
                start: "top top",
                end: "80% top",
                scrub: 0.8,
              },
            },
          );
        });
    }, sectionRef);

    return () => ctx.revert();
  }, [ready]);

  return (
    <section className="hero" ref={sectionRef}>
      <div className="hero__background" ref={backgroundRef}>
        <img src={heroData.backgroundImage} alt="Hero Background" />
      </div>

      <div className="hero__container">
        <div className="hero__content">
          <div className="hero__badge" ref={badgeRef}>
            {heroData.badge}
          </div>

          <h1 className="hero__headline" ref={headlineRef}>
            {heroData.headline}
          </h1>

          <p className="hero__subheadline" ref={subheadlineRef}>
            {heroData.subheadline}
          </p>

          <div className="hero__buttons" ref={buttonsRef}>
            {heroData.buttons.map((button, index) => (
              <Button
                key={index}
                text={button.text}
                variant={button.variant}
                link={button.link}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
